import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { BUSINESS, SERVICES } from "../data/site";
import { Logo } from "./Logo";
import { useLanguage, usePath } from "../i18n/LanguageContext";

const FOOTER_SERVICE_SLUGS = SERVICES.filter((s) => s.slug !== "heat-treated-pallets");

export const Footer = () => {
  const { t } = useLanguage();
  const p = usePath();

  const QUICK_LINKS = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/products", label: t.nav.products, key: "products" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/about", label: t.nav.about, key: "about" },
    { to: "/contact", label: t.nav.contact, key: "contact" },
    { to: "/contact", label: t.nav.quote, key: "request-a-quote" },
  ];

  return (
    <footer className="bg-slate-950 text-slate-400" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-4 mb-5">
            <Logo className="h-16 w-16" testid="footer-logo" />
            <span className="leading-none">
              <span className="block font-heading font-extrabold tracking-tight text-white text-lg">PALLET WORKS</span>
              <span className="block font-mono2 text-[10px] tracking-[0.35em] text-amber-500 font-semibold mt-0.5">LOGISTIC</span>
            </span>
          </div>
          <p className="text-sm leading-relaxed">{t.footer.description}</p>
        </div>

        <div>
          <h4 className="font-mono2 text-xs uppercase tracking-[0.2em] text-amber-500 font-semibold mb-5">{t.footer.quickLinks}</h4>
          <ul className="space-y-2.5 text-sm">
            {QUICK_LINKS.map((l) => (
              <li key={l.key}>
                <Link to={p(l.to)} className="hover:text-amber-400 transition-colors" data-testid={`footer-link-${l.key}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono2 text-xs uppercase tracking-[0.2em] text-amber-500 font-semibold mb-5">{t.footer.services}</h4>
          <ul className="space-y-2.5 text-sm">
            {FOOTER_SERVICE_SLUGS.map((s) => (
              <li key={s.slug}>
                <Link to={p(`/services#${s.slug}`)} className="hover:text-amber-400 transition-colors" data-testid={`footer-service-${s.slug}`}>
                  {t.services[s.slug].title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono2 text-xs uppercase tracking-[0.2em] text-amber-500 font-semibold mb-5">{t.footer.contactInfo}</h4>
          <ul className="space-y-3.5 text-sm">
            <li className="flex gap-3">
              <MapPin className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <span>{BUSINESS.address1}<br />{BUSINESS.address2}</span>
            </li>
            <li>
              <a href={BUSINESS.phoneHref} className="flex gap-3 hover:text-amber-400 transition-colors" data-testid="footer-phone-link">
                <Phone className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {BUSINESS.phone}
              </a>
            </li>
            <li>
              <a href={BUSINESS.emailHref} className="flex gap-3 hover:text-amber-400 transition-colors" data-testid="footer-email-link">
                <Mail className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" /> {BUSINESS.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <span data-testid="footer-copyright">© {new Date().getFullYear()} Pallet Works Logistic. {t.footer.rights}</span>
          <span className="font-mono2 uppercase tracking-widest">{t.footer.location}</span>
        </div>
      </div>
    </footer>
  );
};
